import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { MapPin } from 'lucide-react';
import { isGoogleMapsLoaded, getDescriptionFromSuggestion } from '@/components/ui/LocationPicker/utils';

const MOBILE_MQ = '(max-width: 768px)';

/** A Places API autocomplete suggestion; only the fields read here are typed. */
type PlaceSuggestion = Record<string, unknown> & {
  placeId?: string;
  placePrediction?: { placeId?: string };
};

interface LocationAutocompleteProps {
  value: string;
  onChange: (location: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
}

export const LocationAutocomplete = ({
  value,
  onChange,
  placeholder = 'Search for a city...',
  disabled = false,
  id,
  name,
}: LocationAutocompleteProps) => {
  const [searchQuery, setSearchQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const hasUserInteractedRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setSearchQuery(value);
  }, [value]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  const searchGooglePlaces = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!isGoogleMapsLoaded()) {
      return;
    }

    setIsSearching(true);
    try {
      const placesLib = await window.google.maps.importLibrary('places') as {
        AutocompleteSuggestion: { fetchAutocompleteSuggestions: (r: object) => Promise<{ suggestions: PlaceSuggestion[] }> };
      };
      const request = { input: query, includedPrimaryTypes: ['locality'] as const };

      const response = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      
      if (response && response.suggestions && response.suggestions.length > 0) {
        setSuggestions(response.suggestions.slice(0, 5));
        updateDropdownPosition();
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!hasUserInteractedRef.current) return;
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const runSearch = () => searchGooglePlaces(searchQuery);
    const timeoutId = setTimeout(() => {
      if (isGoogleMapsLoaded()) {
        runSearch();
        return;
      }
      let attempts = 0;
      const maxAttempts = 50;
      pollIntervalRef.current = setInterval(() => {
        attempts += 1;
        if (isGoogleMapsLoaded() || attempts >= maxAttempts) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (isGoogleMapsLoaded()) runSearch();
        }
      }, 150);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    hasUserInteractedRef.current = true;
    setSearchQuery(newValue);
    onChange(newValue);
    setSelectedIndex(-1);
  };

  const handleSelectSuggestion = async (suggestion: PlaceSuggestion, index: number) => {
    const placeId = suggestion.placePrediction?.placeId || suggestion.placeId;
    const suggestionText = getDescriptionFromSuggestion(suggestion, index);
    
    if (!isGoogleMapsLoaded() || !placeId || placeId.startsWith('result-')) {
      onChange(suggestionText);
      setSearchQuery(suggestionText);
      setShowSuggestions(false);
      return;
    }

    try {
      const { Place } = await window.google.maps.importLibrary('places') as {
        Place: new (o: { id: string }) => {
          fetchFields: (o: { fields: string[] }) => Promise<void>;
          formattedAddress?: string;
          displayName?: string;
        };
      };
      const place = new Place({ id: placeId });
      
      await place.fetchFields({ fields: ['formattedAddress', 'displayName'] });
      
      const locationName = place.formattedAddress || place.displayName || suggestionText || searchQuery;
      onChange(locationName);
      setSearchQuery(locationName);
      setShowSuggestions(false);
    } catch (error) {
      onChange(suggestionText);
      setSearchQuery(suggestionText);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex], selectedIndex);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !(target instanceof Element && target.closest('[data-location-suggestions]'))
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobile || !showSuggestions) return;

    let rafId = 0;
    const reposition = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateDropdownPosition);
    };

    document.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    window.visualViewport?.addEventListener('scroll', reposition);
    window.visualViewport?.addEventListener('resize', reposition);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      window.visualViewport?.removeEventListener('scroll', reposition);
      window.visualViewport?.removeEventListener('resize', reposition);
    };
  }, [isMobile, showSuggestions, updateDropdownPosition]);

  useEffect(() => {
    if (!isMobile || !showSuggestions || suggestions.length === 0) return;

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        suggestionsRef.current?.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isMobile, showSuggestions, suggestions.length]);

  const renderSuggestionItems = () =>
    suggestions.map((suggestion, index) => (
      <chakra.button
        key={index}
        type="button"
        w="full"
        display="flex"
        alignItems="center"
        gap="2.5"
        p="3"
        border="none"
        bg={index === selectedIndex ? 'volyx.lightGray' : 'transparent'}
        cursor="pointer"
        textAlign="left"
        fontFamily="body"
        fontSize="sm"
        color="volyx.text"
        transition="colors 0.2s"
        _first={{ borderTopRadius: '10px' }}
        _last={{ borderBottomRadius: '10px' }}
        _hover={{ bg: 'volyx.lightGray' }}
        onClick={() => handleSelectSuggestion(suggestion, index)}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <Box color="volyx.textSecondary" flexShrink={0}>
          <MapPin size={16} />
        </Box>
        <Text as="span" flex="1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {getDescriptionFromSuggestion(suggestion, index)}
        </Text>
      </chakra.button>
    ));

  const suggestionsDropdown = showSuggestions && suggestions.length > 0 && (
    <Box
      ref={suggestionsRef}
      data-location-suggestions
      bg="white"
      border="1px solid"
      borderColor="volyx.borderLight"
      borderRadius="10px"
      boxShadow="lg"
      zIndex={10002}
      maxH={isMobile ? 'min(300px, 40vh)' : '300px'}
      overflowY="auto"
      overscrollBehavior="contain"
      {...(isMobile
        ? {
            position: 'absolute' as const,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            w: 'full',
          }
        : {
            mt: '1',
            style: dropdownPosition
              ? {
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }
              : undefined,
          })}
    >
      {renderSuggestionItems()}
    </Box>
  );

  const searchingDropdown = isSearching && (isMobile || dropdownPosition) && (
    <Box
      p="3"
      bg="white"
      border="1px solid"
      borderColor="volyx.borderLight"
      borderRadius="10px"
      fontFamily="body"
      fontSize="sm"
      color="volyx.textSecondary"
      textAlign="center"
      zIndex={10002}
      boxShadow="lg"
      {...(isMobile
        ? {
            position: 'absolute' as const,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            w: 'full',
          }
        : {
            mt: '1',
            style: dropdownPosition
              ? {
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }
              : undefined,
          })}
    >
      Searching...
    </Box>
  );

  return (
    <Box position="relative" w="full" ref={containerRef} zIndex={showSuggestions ? 10001 : undefined}>
      <Flex position="relative" alignItems="center">
        <chakra.input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            updateDropdownPosition();
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          w="full"
          h="14"
          bg="volyx.lightGray"
          borderRadius="xl"
          border="none"
          px="4"
          pr="10"
          fontSize="md"
          fontFamily="body"
          color="volyx.text"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
          transition="all 0.2s"
          _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          disabled={disabled}
          autoComplete="off"
        />
        <Box position="absolute" right="3" color="volyx.textSecondary" pointerEvents="none" flexShrink={0}>
          <MapPin size={20} />
        </Box>
      </Flex>

      {isMobile ? (
        <>
          {suggestionsDropdown}
          {searchingDropdown}
        </>
      ) : (
        <>
          {suggestionsDropdown && dropdownPosition && createPortal(suggestionsDropdown, document.body)}
          {searchingDropdown && createPortal(searchingDropdown, document.body)}
        </>
      )}
    </Box>
  );
};
